import {
  type JWTClient,
  type JWTCurrentToken,
} from "@queen-one/jwt-http-client";
import axios from "axios";
import React, {
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { AuthContext, type AuthContextType } from "./AuthContext";
import {
  clearJWTClient,
  getJWTClient,
  isCurrentJWTClient,
  logoutJWT,
} from "../api/connectClient";
import type {
  AuthUserJWT,
  AuthResponse,
  LoginRequest,
  LoginResult,
  AuthState,
  APISite,
  Site,
} from "../types/auth";

import { exportTracker } from "@/features/Exports/exportTracker";
import { clearAllOnboardingDrafts } from "@/features/Onboarding/utils/onboardingDraftStorage";
import { clearLastSelectedSiteId } from "@/hooks/useLastSelectedSite";
import { queryClient } from "@/query";
import { DEMO_ROUTE_PATH } from "@/utils/demoLink";

const USER_STORAGE_KEY = "qo_connect_user";
const SITES_STORAGE_KEY = "qo_connect_sites";
const JWT_STORAGE_KEY = "qo_connect_jwt_token";

const API_BASE_URL: string =
  (import.meta.env.VITE_CONNECT_API_URL as string) || "";

/** Process raw API sites into the format used by the app */
function processSites(apiSites: APISite[]): Site[] {
  return apiSites.map((site) => ({
    id: site.id,
    name: site.brand_name ?? "",
  }));
}

type AuthAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_USER"; payload: AuthUserJWT | null }
  | { type: "SET_SITES"; payload: Site[] }
  | { type: "SET_AUTHENTICATED"; payload: boolean }
  | { type: "LOGOUT" };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_USER":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
      };
    case "SET_SITES":
      return { ...state, sites: action.payload };
    case "SET_AUTHENTICATED":
      return { ...state, isAuthenticated: action.payload };
    case "LOGOUT":
      return {
        user: null,
        sites: [],
        isAuthenticated: false,
        isLoading: false,
      };
    default:
      return state;
  }
};

const getStoredUser = (): AuthUserJWT | null => {
  try {
    const user = localStorage.getItem(USER_STORAGE_KEY);
    if (!user) return null;
    const parsedUser = JSON.parse(user) as AuthUserJWT;
    return parsedUser;
  } catch {
    return null;
  }
};

const setStoredUser = (user: AuthUserJWT | null): void => {
  try {
    if (!user) {
      localStorage.removeItem(USER_STORAGE_KEY);
    } else {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    }
  } catch (error) {
    console.error("Error setting stored user", error);
  }
};

const getStoredSites = (): Site[] => {
  try {
    const sites = localStorage.getItem(SITES_STORAGE_KEY);
    if (sites) {
      return JSON.parse(sites) as Site[];
    }

    // Fallback: check for legacy sites data in stored user [deprecated]
    const storedUser = localStorage.getItem(USER_STORAGE_KEY);
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser) as AuthUserJWT & {
        sites?: Site[];
      };
      if (parsedUser.sites && Array.isArray(parsedUser.sites)) {
        setStoredSites(parsedUser.sites);
        return parsedUser.sites;
      }
    }

    return [];
  } catch {
    return [];
  }
};

const setStoredSites = (sites: Site[]): void => {
  try {
    if (sites.length === 0) {
      localStorage.removeItem(SITES_STORAGE_KEY);
    } else {
      localStorage.setItem(SITES_STORAGE_KEY, JSON.stringify(sites));
    }
  } catch (error) {
    console.error("Error setting stored sites", error);
  }
};

const clearStoredJwtToken = (): void => {
  try {
    localStorage.removeItem(JWT_STORAGE_KEY);
  } catch {
    // Storage can be unavailable/restricted in some browser contexts.
  }
};

/**
 * Routes that must paint immediately and never wait on a session probe. The
 * boot overlay has a 250ms show threshold and a 750ms minimum-visible floor
 * (createBootLoader), so probing here would put a full second of video in
 * front of the app's own front door to serve a corner case. `/demo` mints its
 * own session and would race the probe.
 */
const PUBLIC_PATHS = new Set([
  "/login",
  "/forgot-password",
  "/reset-password",
  DEMO_ROUTE_PATH,
]);

/** How long to wait on the boot refresh before giving up and rendering. */
const BOOTSTRAP_TIMEOUT_MS = 4000;

/**
 * Whether to try a silent refresh before first paint. The refresh cookie is
 * HttpOnly, so a browser can hold a perfectly valid session while localStorage
 * is empty — Safari's ITP evicts script-writable storage after 7 days but
 * leaves a server-set first-party cookie alone. Without this probe that
 * browser lands on /login with a live session it cannot see.
 */
function shouldBootstrapSession(): boolean {
  // connectClient.ts sets the precedent: no background auth traffic in tests.
  if (import.meta.env.MODE === "test") return false;
  if (getStoredUser()) return false;
  const pathname = globalThis.location?.pathname ?? "";
  return !PUBLIC_PATHS.has(pathname.replace(/\/+$/, "") || "/");
}

/**
 * getJWTToken() is a bare `axios.post` with no timeout, so a wedged sphinx
 * would otherwise pin the boot overlay open forever.
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("Session bootstrap timed out")),
      ms,
    );
    promise.then(resolve, reject).finally(() => clearTimeout(timer));
  });
}

const storedUser = getStoredUser();

const initialState: AuthState = {
  user: storedUser,
  sites: getStoredSites(),
  isAuthenticated: !!storedUser,
  isLoading: false,
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const setUserOnRefresh = useCallback(
    (token: JWTCurrentToken, responseData?: { sites?: APISite[] }) => {
      const user = token?.decoded as AuthUserJWT | undefined;
      if (!user) {
        throw new Error("Failed to decode user from JWT token");
      }
      dispatch({ type: "SET_USER", payload: user });
      setStoredUser(user);

      // Mirror the response's sites even when it carries none. Leaving the
      // previous session's sites in place would let home resolution redirect
      // to a siteId this token does not cover — reachable whenever a refresh
      // hydrates a different identity than the one already in storage.
      const sites = processSites(responseData?.sites ?? []);
      dispatch({ type: "SET_SITES", payload: sites });
      setStoredSites(sites);
    },
    [],
  );

  // Initialize JWT client with refresh hook (for page reloads with existing token)
  if (state.isAuthenticated) {
    getJWTClient(undefined, setUserOnRefresh);
  }

  const handleTokenResponse = useCallback(
    (data: AuthResponse): LoginResult => {
      if (data.code === "organization_selection_required") {
        if (!data.pending_authentication_token) {
          throw new Error(
            "organization_selection_required response missing pending_authentication_token",
          );
        }
        return {
          kind: "organization_selection_required",
          pendingAuthenticationToken: data.pending_authentication_token,
          organizations: data.organizations ?? [],
        };
      }

      if (data.password_reset) {
        return { kind: "password_reset_success" };
      }

      if (!data.access) {
        throw new Error("Auth response missing access token");
      }

      // A same-document identity switch (opening a demo link in a tab that was
      // already signed in) must not inherit the other identity's cached data.
      // logout() and resetSession() clear it; a successful login has to as well.
      queryClient.clear();
      exportTracker.clear();

      const client = getJWTClient(data.access, setUserOnRefresh);
      if (!client) {
        throw new Error("Failed to create JWT client");
      }

      if (data.sites) {
        const sites = processSites(data.sites);
        dispatch({ type: "SET_SITES", payload: sites });
        setStoredSites(sites);
      }

      // Fresh session — drop any leftover onboarding drafts so it starts clean.
      // Only reached on real login / org-selection / password-reset confirm, not
      // on page refresh, so an in-progress draft survives a reload.
      clearAllOnboardingDrafts();

      return { kind: "authenticated" };
    },
    [setUserOnRefresh],
  );

  const upsertSite = useCallback(
    (site: Site) => {
      const idx = state.sites.findIndex((s) => s.id === site.id);
      const next =
        idx === -1
          ? [...state.sites, site]
          : state.sites.map((s) => (s.id === site.id ? site : s));
      dispatch({ type: "SET_SITES", payload: next });
      setStoredSites(next);
    },
    [state.sites],
  );

  const resetAuthState = useCallback(() => {
    dispatch({ type: "LOGOUT" });
    queryClient.clear();
    exportTracker.clear();
    setStoredUser(null);
    setStoredSites([]);
    clearStoredJwtToken();
  }, []);

  /**
   * Wipes every trace of the local session: React state, cached queries,
   * stored user/sites, the JWT client (and its armed refresh timer) and the
   * last-selected site. This is what logout() does minus the network call, and
   * what /demo needs before minting a demo session in a browser that may still
   * be holding a real one.
   *
   * resetAuthState() stays separate for the login/org-select/password-reset
   * catch paths: those must not clear the last-selected site over a mistyped
   * password.
   */
  const resetSession = useCallback(() => {
    resetAuthState();
    clearJWTClient();
    clearLastSelectedSiteId();
  }, [resetAuthState]);

  // Login function
  const login = useCallback(
    async (credentials: LoginRequest): Promise<LoginResult> => {
      dispatch({ type: "SET_LOADING", payload: true });

      try {
        const tokenPath = import.meta.env.VITE_JWT_TOKEN_PATH as string;
        if (!tokenPath) {
          throw new Error(
            "VITE_JWT_TOKEN_PATH environment variable is not set",
          );
        }

        const response = await axios<AuthResponse>({
          method: "POST",
          url: tokenPath,
          data: credentials,
        });

        return handleTokenResponse(response.data);
      } catch (error) {
        resetAuthState();
        throw error;
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [handleTokenResponse, resetAuthState],
  );

  const selectOrganization = useCallback(
    async (
      organizationId: string,
      pendingAuthenticationToken: string,
    ): Promise<LoginResult> => {
      dispatch({ type: "SET_LOADING", payload: true });

      try {
        const response = await axios<AuthResponse>({
          method: "POST",
          url: `${API_BASE_URL}/auth/select-organization`,
          data: {
            organization_id: organizationId,
            pending_authentication_token: pendingAuthenticationToken,
          },
        });

        return handleTokenResponse(response.data);
      } catch (error) {
        resetAuthState();
        throw error;
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [handleTokenResponse, resetAuthState],
  );

  const confirmPasswordReset = useCallback(
    async (token: string, newPassword: string): Promise<LoginResult> => {
      dispatch({ type: "SET_LOADING", payload: true });

      try {
        const response = await axios<AuthResponse>({
          method: "POST",
          url: `${API_BASE_URL}/auth/password-reset/confirm`,
          data: { token, new_password: newPassword },
        });

        return handleTokenResponse(response.data);
      } catch (error) {
        resetAuthState();
        throw error;
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [handleTokenResponse, resetAuthState],
  );

  const sendPasswordReset = useCallback(
    async (email: string): Promise<void> => {
      await axios.post(`${API_BASE_URL}/auth/password-reset/send`, { email });
    },
    [],
  );

  // Logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      await logoutJWT(`${API_BASE_URL}/token/logout`);
    } catch (error: unknown) {
      // Handle specific error responses from the API
      const axiosError = error as {
        response?: { status?: number; data?: { error?: string } };
      };
      if (axiosError.response?.status === 400) {
        const errorMessage = axiosError.response.data?.error || "Token error";
        console.warn("Logout warning:", errorMessage);
        // Even if the token is already blacklisted/invalid/expired/missing,
        // we should still consider the logout successful from the client perspective
      } else {
        // For other errors, log and still proceed with client-side logout
        console.error(
          "Logout API failed, proceeding with local logout:",
          error,
        );
      }
    } finally {
      // Always perform local cleanup regardless of API call result
      resetSession();
    }
  }, [resetSession]);

  const [isBootstrapping, setIsBootstrapping] = useState(
    shouldBootstrapSession,
  );

  // StrictMode double-invokes effects; the probe must fire exactly once.
  const bootstrapStarted = useRef(false);
  // Set once the probe loses its race. getJWTToken() is a bare axios.post with
  // no abort support, so a timed-out probe keeps running; this is how its
  // result is disowned.
  const bootstrapAbandoned = useRef(false);

  useEffect(() => {
    if (!isBootstrapping || bootstrapStarted.current) return;
    bootstrapStarted.current = true;

    void (async () => {
      try {
        // The hook is wrapped rather than passed directly: if the probe times
        // out and the visitor then signs in as someone else, the abandoned
        // request can still land and would otherwise dispatch the probe's
        // identity over the newer session.
        const client: JWTClient | null = getJWTClient(
          undefined,
          (token: JWTCurrentToken, responseData?: { sites?: APISite[] }) => {
            if (!bootstrapAbandoned.current) {
              setUserOnRefresh(token, responseData);
              return;
            }
            // Landed after the race was lost. storeJWTToken() saves to storage
            // and *then* calls this hook, so the token is already persisted:
            // ignoring it would leave an anonymous UI holding a usable bearer
            // token. Undo it, but only while the probe's own client is still
            // installed — by now a real login may own the session, and
            // clearing that would take a legitimate session down.
            if (isCurrentJWTClient(client)) {
              clearJWTClient();
            }
          },
        );
        if (!client) return;
        // On success the refresh hook hydrates user *and* sites: the proxy
        // re-fetches /v1/sites on every successful auth-route response.
        await withTimeout(client.forceRefresh(), BOOTSTRAP_TIMEOUT_MS);
      } catch {
        // No usable refresh cookie (or sphinx is slow): stay anonymous and let
        // the route guards send the visitor to /login. forceRefresh() already
        // cleared the stored token on failure, and a prospect who never had a
        // session must not see an error.
        bootstrapAbandoned.current = true;
      } finally {
        setIsBootstrapping(false);
      }
    })();
  }, [isBootstrapping, setUserOnRefresh]);

  const value: AuthContextType = {
    ...state,
    isBootstrapping,
    upsertSite,
    login,
    selectOrganization,
    confirmPasswordReset,
    sendPasswordReset,
    logout,
    resetSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
